import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ConfirmationModal from '../../components/ConfirmationModal';
import './Addresses.css';

function Addresses() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    address_type: 'shipping',
    recipient_name: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    phone: '',
    is_default: false
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);

    // Check if user is a customer
    if (parsedUser.role !== 'customer') {
      navigate('/admin/dashboard');
      return;
    }

    setUser(parsedUser);
    fetchAddresses();
  }, [navigate]);

  const fetchAddresses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get('/api/auth/addresses', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setFormData({
      address_type: 'shipping',
      recipient_name: '',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'United States',
      phone: '',
      is_default: false
    });
    setShowAddForm(true);
    setMessage({ type: '', text: '' });
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      address_type: address.address_type,
      recipient_name: address.recipient_name,
      street_address: address.street_address,
      city: address.city,
      state: address.state,
      zip_code: address.zip_code,
      country: address.country,
      phone: address.phone || '',
      is_default: address.is_default === 1
    });
    setShowAddForm(true);
    setMessage({ type: '', text: '' });
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingAddress(null);
    setFormData({
      address_type: 'shipping',
      recipient_name: '',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'United States',
      phone: '',
      is_default: false
    });
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('authToken');

      if (editingAddress) {
        // Update existing address
        await axios.put(
          `/api/auth/addresses/${editingAddress.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ type: 'success', text: 'Address updated successfully!' });
      } else {
        // Add new address
        await axios.post(
          '/api/auth/addresses',
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ type: 'success', text: 'Address added successfully!' });
      }

      // Refresh addresses list
      fetchAddresses();

      // Close form after short delay
      setTimeout(() => {
        handleCancel();
      }, 1500);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save address'
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (addressId) => {
    setDeleteConfirm(addressId);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`/api/auth/addresses/${deleteConfirm}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDeleteConfirm(null);
      setMessage({ type: 'success', text: 'Address deleted successfully!' });
      fetchAddresses();

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      setDeleteConfirm(null);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete address'
      });
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        `/api/auth/addresses/${addressId}/default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: 'Default address updated!' });
      fetchAddresses();

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to set default address'
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="addresses">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="addresses">
      <div className="account-container">
        <div className="account-sidebar">
          <div className="account-user-info">
            <div className="user-avatar">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <h3>{user?.name || 'Customer'}</h3>
            <p>{user?.email}</p>
          </div>

          <nav className="account-nav">
            <Link to="/account" className="account-nav-item">
              Dashboard
            </Link>
            <Link to="/account/orders" className="account-nav-item">
              Order History
            </Link>
            <Link to="/account/addresses" className="account-nav-item active">
              Addresses
            </Link>
            <Link to="/account/wishlist" className="account-nav-item">
              Wishlist
            </Link>
            <Link to="/account/settings" className="account-nav-item">
              Settings
            </Link>
            <button onClick={handleLogout} className="account-nav-item logout-btn">
              Logout
            </button>
          </nav>
        </div>

        <div className="account-main">
          <div className="account-header">
            <div>
              <h1>My Addresses</h1>
              <p>Manage your shipping and billing addresses</p>
            </div>
            {!showAddForm && (
              <button onClick={handleAddNew} className="btn btn-primary">
                + Add New Address
              </button>
            )}
          </div>

          {message.text && (
            <div className={`message-banner ${message.type}`}>
              {message.text}
            </div>
          )}

          {showAddForm && (
            <div className="address-form-section">
              <h2>{editingAddress ? 'Edit Address' : 'Add New Address'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="address_type">Address Type</label>
                    <select
                      id="address_type"
                      name="address_type"
                      value={formData.address_type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="shipping">Shipping</option>
                      <option value="billing">Billing</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="recipient_name">Recipient Name</label>
                    <input
                      type="text"
                      id="recipient_name"
                      name="recipient_name"
                      value={formData.recipient_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="street_address">Street Address</label>
                  <input
                    type="text"
                    id="street_address"
                    name="street_address"
                    value={formData.street_address}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="zip_code">ZIP Code</label>
                    <input
                      type="text"
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <input type="hidden" name="country" value="United States" />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number (Optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_default"
                      checked={formData.is_default}
                      onChange={handleInputChange}
                    />
                    <span>Set as default {formData.address_type} address</span>
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editingAddress ? 'Update Address' : 'Add Address')}
                  </button>
                  <button type="button" onClick={handleCancel} className="btn btn-secondary-outline">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {!showAddForm && addresses.length === 0 ? (
            <div className="empty-state">
              <h3>No Addresses Saved</h3>
              <p>Add your shipping and billing addresses for faster checkout</p>
              <button onClick={handleAddNew} className="btn btn-primary">
                Add Your First Address
              </button>
            </div>
          ) : !showAddForm && (
            <div className="addresses-grid">
              {addresses.map((address) => (
                <div key={address.id} className={`address-card ${address.is_default ? 'default' : ''}`}>
                  {address.is_default === 1 && (
                    <div className="default-badge">Default {address.address_type}</div>
                  )}

                  <div className="address-type">
                    {address.address_type}
                  </div>

                  <div className="address-details">
                    <p className="recipient">{address.recipient_name}</p>
                    <p>{address.street_address}</p>
                    <p>{address.city}, {address.state} {address.zip_code}</p>
                    <p>{address.country}</p>
                    {address.phone && <p className="phone">{address.phone}</p>}
                  </div>

                  <div className="address-actions">
                    {address.is_default !== 1 && (
                      <button
                        onClick={() => handleSetDefault(address.id)}
                        className="btn-link"
                      >
                        Set as Default
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(address)}
                      className="btn-link"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => confirmDelete(address.id)}
                      className="btn-link delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Address"
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              Delete
            </button>
          </>
        }
      >
        <p>Are you sure you want to delete this address?</p>
        <p><strong>This action cannot be undone.</strong></p>
      </ConfirmationModal>
    </div>
  );
}

export default Addresses;
